import { Reducer } from 'interfaces'
import { Member, MemberData, MemberDataState, OnboardingInternalStep } from 'interfaces/member'
import { SET_MEMBER_INFO } from 'actions/memberActions'
import { LOGGED_IN, LOGGED_OUT } from 'actions/authActions'
import { FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { FORM_CONTACT_EDIT, FORM_MEMBER_PROFILE } from 'constants/forms'
import { isSuccessAction, isFailAction } from 'helpers/actionsHelpers'
import { MEMBER_INFO_REFRESH_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { createStaleableReducer } from 'helpers/storeHelpers'
import { GET_FEED_MEMBER_INFO } from 'actions/feedActions'
import { UNAUTHORIZED_STATUS_CODE } from 'constants/ajax'
import { CREATE_STRIPE_PAYMENT_METHOD, PATCH_STRIPE_PAYMENT_METHOD } from 'actions/stripeActions'
import { CANCEL_SUBSCRIPTION, GetSubscriptionAction } from 'actions/subscriptionActions'
import { WsMessageAction } from 'actions/webSocketActions'
import { SubscriptionType } from 'interfaces/subscription'
import { captureWarning } from 'helpers/errorHelpers'
import { CANCEL_ADVISOR_MEETING } from 'components/features/coaching/advisorStore/actions/advisorMeetingActions'
import { GetMeetingsInfoAction } from 'components/features/coaching/advisorStore/actions/advisorPageActions'
import { JoinCCWaitlistAction } from 'components/features/creditCard/creditCardStore/actions'

const getPreviousOnboardingStep = (prevState: MemberData | null, nextMember: Member): OnboardingInternalStep | null => {
  const prevMemberInfo = prevState ? prevState.info : null

  return prevMemberInfo && nextMember.onboarding_step !== prevMemberInfo.onboarding_step
    ? prevMemberInfo.onboarding_step
    : (prevState ? prevState.previousStep : null)
}

const setMemberSubscriptionType = (prevState: MemberData, type: SubscriptionType): MemberData => {
  if (!prevState.info) {
    captureWarning('Updating member subscription on absent member info')
    return prevState
  }

  const prevSubscription = prevState.info.subscription
  if (prevSubscription.type === type) {
    return prevState
  }

  return {
    ...prevState,
    info: {
      ...prevState.info,
      subscription: {
        ...prevSubscription,
        type,
      },
    },
  }
}

const UNAUTHORIZED_MEMBER: MemberData = {
  info: null,
  isAuthenticated: false,
  previousStep: null,
}

export const memberDataReducer: Reducer<MemberData | null> = (prevState = null, action) => {
  if (action.type === GET_FEED_MEMBER_INFO) {
    if (isSuccessAction(action)) {
      const prevInfo = prevState ? prevState.info : null
      const { uid } = action.payload.response.member

      return {
        isAuthenticated: true,
        info: {
          ...prevInfo,
          uid,
        } as Member,
        previousStep: prevState ? prevState.previousStep : null,
      }
    }

    if (isFailAction(action) && action.payload.errorCode === UNAUTHORIZED_STATUS_CODE) {
      return UNAUTHORIZED_MEMBER
    }
  }

  switch (action.type) {
    case SET_MEMBER_INFO: {
      const nextMemberInfo = action.payload
      const prevMemberInfo = prevState ? prevState.info : null

      return {
        info: {
          ...prevMemberInfo,
          ...nextMemberInfo,
        },
        isAuthenticated: true,
        previousStep: getPreviousOnboardingStep(prevState, nextMemberInfo),
      }
    }

    case FORM_SUBMITTED_SUCCESSFULLY: {
      if (action.payload.formName === FORM_CONTACT_EDIT) {
        const prevMemberInfo = prevState ? prevState.info : null
        const nextMemberInfo = action.payload.response

        return {
          info: {
            ...prevMemberInfo,
            ...nextMemberInfo,
          } as Member,
          isAuthenticated: true,
          previousStep: prevState ? prevState.previousStep : null,
        }
      }

      if (action.payload.formName === FORM_MEMBER_PROFILE) {
        const prevMemberInfo = prevState ? prevState.info : null
        const { member } = action.payload.response

        return {
          info: {
            ...prevMemberInfo,
            ...member,
          } as Member,
          isAuthenticated: true,
          previousStep: prevState ? prevState.previousStep : null,
        }
      }

      return prevState
    }

    case LOGGED_IN: {
      return {
        info: null,
        previousStep: null,
        ...prevState,
        isAuthenticated: true,
      }
    }

    case LOGGED_OUT: {
      return UNAUTHORIZED_MEMBER
    }

    case WsMessageAction.type: {
      const message = action.payload
      switch (message.event) {
        case 'subscription_changed': {
          if (!prevState) {
            return prevState
          }

          const { type } = message.data
          return setMemberSubscriptionType(prevState, type)
        }

        case 'meetings_info_changed': {
          if (!prevState?.info) {
            return prevState
          }

          const prevInfo = prevState.info
          const { had_appointed_meeting, is_meeting_eligible } = message.data.meetings_info
          return {
            ...prevState,
            info: {
              ...prevInfo,
              had_appointed_meeting,
              is_meeting_eligible,
            },
          }
        }
      }

      return prevState
    }

    case CREATE_STRIPE_PAYMENT_METHOD:
    case PATCH_STRIPE_PAYMENT_METHOD:
    case GetSubscriptionAction.type:
    case CANCEL_SUBSCRIPTION: {
      if (action.meta !== 'success' || !prevState || !prevState.info) {
        return prevState
      }

      const { type } = action.payload.response
      return setMemberSubscriptionType(prevState, type)
    }

    case CANCEL_ADVISOR_MEETING: {
      if (action.meta !== 'success') {
        return prevState
      }

      if (!prevState?.info) {
        return prevState
      }

      const prevInfo = prevState.info
      return {
        ...prevState,
        info: {
          ...prevInfo,
          had_appointed_meeting: false,
        },
      }
    }

    case JoinCCWaitlistAction.type: {
      if (action.meta !== 'success' || !prevState?.info) {
        return prevState
      }

      return {
        ...prevState,
        info: {
          ...prevState.info,
          has_joined_card_waitlist: true,
        },
      }

    }

    case GetMeetingsInfoAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      if (!prevState?.info) {
        return prevState
      }

      const prevInfo = prevState.info
      const { had_appointed_meeting, is_meeting_eligible } = action.payload.response
      return {
        ...prevState,
        info: {
          ...prevInfo,
          had_appointed_meeting,
          is_meeting_eligible,
        },
      }
    }

    default:
      return prevState
  }
}

const memberReducer: Reducer<MemberDataState> =
  createStaleableReducer(memberDataReducer, MEMBER_INFO_REFRESH_TRIGGERS)

/**
 * createStaleableReducer set isStale to false if data changed, although it's not correct in some cases
 */
const memberReducerRevertIsStale: Reducer<MemberDataState> = (prevState, action) => {
  const prevIsStale = prevState?.isStale ?? false
  prevState = memberReducer(prevState, action)

  switch (action.type) {
    case WsMessageAction.type: {
      const message = action.payload
      switch (message.event) {
        case 'subscription_changed':
        case 'meetings_info_changed': {
          return {
            ...prevState,
            isStale: prevIsStale,
          }
        }
      }

      return prevState
    }
  }
  return prevState
}

export default memberReducerRevertIsStale
